<div class="mistral-dialog">
  <!-- Close (X) -->
  <button class="close-btn" mat-icon-button (click)="close()" aria-label="Close Mistral Large Info">
    <span aria-hidden="true">×</span>
  </button>

  <div class="header">
    <img src="assets/logos/mistral.svg" alt="Mistral Large Logo" class="logo" />
    <h2>Mistral Large</h2>
    <div class="sub">
      Precision + speed + multilingual strength for production chat, writing, and structured workflows
    </div>
  </div>

  <div class="body">
    <p>
      <strong>Mistral Large</strong> is a high-performance, general-purpose model designed for teams that need
      <strong>fast responses</strong>, <strong>reliable instruction-following</strong>, and
      <strong>high-quality multilingual output</strong>.
      It’s especially effective for business environments where you want a model that can move between
      <strong>natural conversation</strong>, <strong>technical problem solving</strong>, and
      <strong>structured outputs</strong> without losing consistency.
      <br /><br />
      If your users are doing real work—summarizing meetings, drafting client-ready content, extracting fields from text,
      or turning messy notes into clean action plans—Mistral Large tends to deliver
      <strong>clear, usable results</strong> with minimal prompt babysitting.
    </p>

    <ul>
      <li>🌍 <strong>Multilingual Excellence:</strong> Natural writing and translation across many languages—great for global teams, localization, and multi-region support.</li>
      <li>⚡ <strong>Fast & Efficient:</strong> Low-latency answers that work well for chat UX, agent loops, automation, and high-throughput workflows.</li>
      <li>🧩 <strong>Instruction Following:</strong> Strong at constraint-based prompts (“follow these rules”, “use this format”, “don’t include X”) and multi-step tasks.</li>
      <li>🧾 <strong>Structured Output:</strong> Reliable for repeatable formatting—bullets, templates, summaries, and JSON-style extraction for downstream systems.</li>
      <li>✍️ <strong>Business-Grade Writing:</strong> Produces clean tone control (formal, friendly, concise) for emails, memos, proposals, and internal docs.</li>
      <li>🧑‍💻 <strong>Technical Utility:</strong> Helpful for debugging explanations, code snippets, refactors, and “walk me through the logic” reasoning.</li>
      <li>🔒 <strong>Enterprise Consistency:</strong> Predictable outputs and stable style—useful when you need fewer surprises in production and user-facing apps.</li>
    </ul>

    <div class="optimal">
      <h3>What Mistral Large is best at</h3>
      <ul>
        <li>✔️ Multilingual chat, rewriting, localization, and translation with a natural tone</li>
        <li>✔️ Summaries that preserve the important details (decisions, risks, owners, next steps)</li>
        <li>✔️ Turning messy input into structure: fields, checklists, action items, and clean templates</li>
        <li>✔️ Business writing with consistent voice: concise, professional, or friendly on demand</li>
        <li>✔️ Technical Q&A and problem decomposition: explain, propose fixes, list steps, validate assumptions</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>When Mistral Large is the right pick</h3>
      <ul>
        <li>🗣️ You want <strong>fast, high-quality conversation</strong> that still feels smart and grounded</li>
        <li>📄 You need <strong>reliable formatting</strong> for production flows (bullets, schemas, extraction)</li>
        <li>🌍 Your users operate in <strong>multiple languages</strong> (or mixed-language context)</li>
        <li>⚙️ You’re building <strong>workflows</strong>: summarize → extract → draft → refine → finalize</li>
        <li>📈 You care about <strong>throughput and consistency</strong> for day-to-day business use</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Recommended production usage pattern</h3>
      <ul>
        <li>1) Use <strong>Mistral Large</strong> as the default for chat, writing, summarization, and extraction</li>
        <li>2) Ask for a <strong>fixed format</strong> (template / bullet schema / JSON) to keep outputs stable</li>
        <li>3) Add <strong>light validation</strong> for critical flows (required fields present, length limits, policy rules)</li>
        <li>4) Escalate to a deeper model only when tasks become unusually complex or high-stakes</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Where it shines the most</h3>
      <ul>
        <li>🧑‍💼 <strong>Ops & support:</strong> ticket replies, escalation briefs, knowledge-base responses, SOP drafts</li>
        <li>📚 <strong>Knowledge work:</strong> summarizing docs, synthesizing sources, converting notes into plans</li>
        <li>🧾 <strong>Extraction:</strong> pulling structured fields from text, emails, forms, and long documents</li>
        <li>🧑‍💻 <strong>Engineering helper:</strong> quick code drafting, bug explanation, refactor guidance, checklists</li>
        <li>🌍 <strong>Global content:</strong> localized messaging, multilingual FAQs, region-specific variants</li>
      </ul>
    </div>
  </div>
</div>
